import * as Sentry from '@sentry/react'
import { signInWithCustomToken } from 'firebase/auth'
import { createBrowserRouter, LoaderFunctionArgs, redirect } from 'react-router-dom'

import ErrorPage from './ErrorPage'
import { auth } from './firebase.ts'
import { Root } from './routes/Root'
import {
  AUTH_QUERY_KEY,
  fetchUserSession,
  fetchWhoIAm,
  WHO_I_AM_QUERY_KEY
} from './store/authStore'
import { queryClient } from './store/queryClient'
import { toLoggableError } from './utils/UserReadableDescription'

// ─── Cache helpers ────────────────────────────────────────────────────────────

const resolveSessionFromCache = async () => {
  const cached = queryClient.getQueryData(AUTH_QUERY_KEY)
  if (cached !== undefined) return cached

  return queryClient.fetchQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchUserSession
  })
}

const resolveWhoIAmFromCache = async () => {
  const cached = queryClient.getQueryData(WHO_I_AM_QUERY_KEY)
  if (cached !== undefined) return cached

  return queryClient.fetchQuery({
    queryKey: WHO_I_AM_QUERY_KEY,
    queryFn: fetchWhoIAm
  })
}

// ─── Loaders ─────────────────────────────────────────────────────────────────

const rootIndexLoader = () => {
  if (window.location.pathname === '/') return redirect('/dashboard')
  return null
}

const authLoader = async () => {
  const session = await resolveSessionFromCache()
  if (session) return redirect('/dashboard')
  return null
}

const dashboardLoader = async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const idToken = urlParams.get('token')

  if (idToken) {
    await signInWithCustomToken(auth, idToken)
    queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY })
  }

  const session = await resolveSessionFromCache()
  if (!session) return redirect('/auth/login')

  try {
    await resolveWhoIAmFromCache()
  } catch (error) {
    console.error(toLoggableError(error, 'No se pudo obtener el perfil del usuario'))
    return redirect('/auth/login')
  }

  return null
}

// Starts validating the invitation code while the page's chunk and translations
// are still downloading. Doesn't wait for the result: the page picks it up.
const invitationLoader = ({ request }: LoaderFunctionArgs) => {
  const search = new URL(request.url).search
  import('./store/promoCodeCheck.ts')
    .then(({ getPromoCodeFromSearch, prefetchPromoCode }) => {
      const code = getPromoCodeFromSearch(search)
      if (code) prefetchPromoCode(code)
    })
    .catch(() => {}) // the page runs the check itself
  return null
}

// main.tsx reloads the page on `vite:preloadError` (a chunk of a previous deploy
// that's gone). Preventing that error makes Vite resolve the import with
// `undefined`, and reading the page component off it threw before the reload
// landed. Never settle instead: the reload is already on its way.
const holdWhileReloading = <T,>(module: T | undefined): Promise<T> =>
  module === undefined ? new Promise<T>(() => {}) : Promise.resolve(module)

// ─── Router ───────────────────────────────────────────────────────────────────

// Every page except Root is loaded with `lazy`, so a visitor only downloads the
// code of the route they land on (e.g. the invitation landing doesn't pull in
// the TripBook editor). The router keeps the current page on screen while the
// next route's chunk is loading.

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        loader: rootIndexLoader
      },
      {
        path: 't/:id',
        lazy: () =>
          import('./routes/DeepLinkMobileScreen.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'l/:id',
        lazy: () =>
          import('./routes/DeepLinkMobileScreen.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'auth',
        lazy: () =>
          import('./routes/auth/AuthPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.AuthPage })),
        loader: authLoader,
        children: [
          {
            path: 'forgotten-password',
            lazy: () =>
              import('./routes/auth/ForgottenPasswordPage.tsx')
                .then(holdWhileReloading)
                .then((m) => ({
                  Component: m.ForgottenPasswordPage
                }))
          },
          {
            path: ':tab',
            lazy: () =>
              import('./routes/auth/AuthActionPage.tsx').then(holdWhileReloading).then((m) => ({
                Component: m.AuthActionPage
              }))
          }
        ]
      },
      {
        path: 'create',
        lazy: () =>
          import('./routes/TripCreatePage.tsx').then(holdWhileReloading).then((m) => ({
            element: <m.TripCreatePage mode="create-trip" />
          }))
      },
      {
        path: 'checkout/:orderId/finish',
        lazy: () =>
          import('./routes/CheckoutFinishPage.tsx').then(holdWhileReloading).then((m) => ({
            Component: m.CheckoutFinishPage
          }))
      },
      {
        path: 'add',
        lazy: () =>
          import('./routes/TripCreatePage.tsx').then(holdWhileReloading).then((m) => ({
            element: <m.TripCreatePage mode="add-images-to-trip" />
          }))
      },
      {
        path: 'attach-images',
        lazy: () =>
          import('./routes/TripCreatePage.tsx').then(holdWhileReloading).then((m) => ({
            element: <m.TripCreatePage mode="attach-images" />
          }))
      },
      {
        path: 'comenzamos',
        lazy: () =>
          import('./routes/TripIntroPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'invitacion/:code',
        loader: invitationLoader,
        lazy: () =>
          import('./routes/InvitationPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'invitacion',
        loader: invitationLoader,
        lazy: () =>
          import('./routes/InvitationPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'invitation',
        loader: invitationLoader,
        lazy: () =>
          import('./routes/InvitationPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'primeros-pasos',
        lazy: () =>
          import('./routes/B2COnboardingPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'tripbook-proposal/:tripID',
        lazy: () =>
          import('./routes/TripBookPreviewPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        // Public review link. The only unauthenticated page in the app: the
        // token in the URL is the whole credential, and the API resolves it to
        // exactly one tripBook.
        path: 'review/:shareToken',
        lazy: () =>
          import('./routes/TripBookReviewPage.tsx').then(holdWhileReloading).then((m) => ({
            Component: m.TripBookReviewPage
          }))
      },
      {
        path: 'payment/confirm',
        lazy: () =>
          import('./routes/PaymentConfirmPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'poster',
        lazy: () =>
          import('./routes/PosterCreator.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'upload',
        lazy: () =>
          import('./routes/ImageUploadScreen.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'trips/:id/preview',
        lazy: () =>
          import('./routes/PublicTripPreviewPage.tsx').then(holdWhileReloading).then((m) => ({
            Component: m.PublicTripPreviewPage
          }))
      },
      {
        path: 'trips/:id/flipbook',
        lazy: () =>
          import('./routes/FlipBookPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.default }))
      },
      {
        path: 'checkout/:orderId',
        lazy: () =>
          import('./routes/CheckoutPage.tsx')
            .then(holdWhileReloading)
            .then((m) => ({ Component: m.CheckoutPage })),
        children: [
          {
            path: ':step',
            lazy: () =>
              import('./routes/CheckoutPage.tsx')
                .then(holdWhileReloading)
                .then((m) => ({ Component: m.CheckoutPage }))
          }
        ]
      },
      {
        path: 'dashboard',
        lazy: () =>
          import('./routes/dashboard/ProtectedDashboardLayout.tsx')
            .then(holdWhileReloading)
            .then((m) => ({
              Component: m.ProtectedDashboardLayout
            })),
        loader: dashboardLoader,
        children: [
          {
            path: '',
            lazy: () =>
              import('./routes/TripsListPage.tsx')
                .then(holdWhileReloading)
                .then((m) => ({ Component: m.TripsListPage }))
          },
          {
            path: 'trip/:id/tripbook/:tripBookId?/editor',
            lazy: () =>
              import('./routes/TripBookEditorPage.tsx').then(holdWhileReloading).then((m) => ({
                Component: m.TripBookEditorPage
              })),
            children: [
              {
                path: 'preview',
                lazy: () =>
                  import('./routes/TripPreviewPage.tsx')
                    .then(holdWhileReloading)
                    .then((m) => ({ Component: m.default }))
              },
              {
                path: 'organise-days',
                lazy: () =>
                  import('./routes/TripOrganisePage.tsx')
                    .then(holdWhileReloading)
                    .then((m) => ({ Component: m.default }))
              }
            ]
          },
          {
            // Photo-curation report viewer for the design team.
            path: 'trip/:id/curation',
            lazy: () =>
              import('./routes/TripBookCurationPage.tsx').then(holdWhileReloading).then((m) => ({
                Component: m.TripBookCurationPage
              }))
          },
          {
            path: 'trip/:id/tripbook/:tripBookId?/review',
            lazy: () =>
              import('./routes/TripBookReviewPage.tsx').then(holdWhileReloading).then((m) => ({
                Component: m.TripBookReviewPage
              }))
          },
          {
            path: 'trip/:id',
            lazy: () =>
              import('./routes/TripBookEditorPage.tsx').then(holdWhileReloading).then((m) => ({
                Component: m.TripBookEditorPage
              })),
            children: [
              {
                path: 'preview',
                lazy: () =>
                  import('./routes/TripPreviewPage.tsx')
                    .then(holdWhileReloading)
                    .then((m) => ({ Component: m.default }))
              },
              {
                path: 'organise-days',
                lazy: () =>
                  import('./routes/TripOrganisePage.tsx')
                    .then(holdWhileReloading)
                    .then((m) => ({ Component: m.default }))
              }
            ]
          },
          {
            path: 'checkout/:orderId',
            lazy: () =>
              import('./routes/CheckoutPage.tsx')
                .then(holdWhileReloading)
                .then((m) => ({ Component: m.CheckoutPage })),
            children: [
              {
                path: ':step',
                lazy: () =>
                  import('./routes/CheckoutPage.tsx')
                    .then(holdWhileReloading)
                    .then((m) => ({ Component: m.CheckoutPage }))
              }
            ]
          }
        ]
      }
    ]
  }
])
